/**
 * Saudi / Qawaem ERP — default application & company settings.
 * Single source of truth for frontend defaults (settingsService, SettingsContext).
 */
import { SAUDI_BRAND } from '../../products/saudi/config/saudiBrandConfig';
import { SAUDI_COUNTRY_VALUE, SAUDI_PHONE_PREFIX } from '../utils/brandCountry';
import { SAUDI_REGIONS } from '../utils/brandRegions';
import { getBrandVatRate } from '../utils/brandCurrency';

export const SAUDI_SETTINGS_DEFAULTS = {
  locale: 'ar-SA',
  language: 'ar',
  currency: SAUDI_BRAND.currency || 'SAR',
  currencySymbol: 'ر.س',
  vatRate: SAUDI_BRAND.vatRate || 15,
  phonePrefix: SAUDI_PHONE_PREFIX,
  weekend: ['friday', 'saturday'],
  workingDays: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
  dateFormat: 'gregorian',
  showHijri: true,
  fiscalYear: 'calendar',
  vatEnabled: true,
};

/** Saudi commercial banks for company settings forms. */
export const SAUDI_BANKS_LIST = [
  { code: 'SNB', name: 'البنك الأهلي السعودي' },
  { code: 'RJHI', name: 'مصرف الراجحي' },
  { code: 'RIBL', name: 'بنك الرياض' },
  { code: 'BSFR', name: 'البنك السعودي الفرنسي' },
  { code: 'SAIB', name: 'البنك السعودي للاستثمار' },
  { code: 'BLAD', name: 'بنك البلاد' },
  { code: 'BJAZ', name: 'بنك الجزيرة' },
  { code: 'ANB', name: 'البنك العربي الوطني' },
  { code: 'GIB', name: 'بنك الخليج الدولي — السعودية' },
  { code: 'INMA', name: 'بنك الإنماء' },
];

/** Map SAUDI_REGIONS to wilaya-like rows for SettingsBody selects. */
export function getSaudiRegionsAsWilayaRows(regions) {
  const list = regions || SAUDI_REGIONS;
  return list.map((r, i) => ({
    id: i + 1,
    code: String(i + 1).padStart(2, '0'),
    fr: r.label,
    ar: r.label,
  }));
}

/** VAT TRN: 15 digits starting with 3 (ZATCA). */
export function isValidSaudiVatNumber(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return /^3\d{14}$/.test(digits);
}

/** Commercial registration (CR): 10 digits. */
export function isValidSaudiCommercialRegistration(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return /^\d{10}$/.test(digits);
}

/** GOSI establishment number: 9–10 digits. */
export function isValidGosiEstablishmentNumber(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return /^\d{9,10}$/.test(digits);
}

/** Saudi IBAN: SA + 22 digits. */
export function isValidSaudiIban(value) {
  const normalized = String(value || '').replace(/\s/g, '').toUpperCase();
  return /^SA\d{22}$/.test(normalized);
}

/** Saudi mobile: +966 5X XXX XXXX */
export function isValidSaudiPhone(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.startsWith('966')) {
    return /^9665\d{8}$/.test(digits);
  }
  if (digits.startsWith('05')) {
    return /^05\d{8}$/.test(digits);
  }
  return /^5\d{8}$/.test(digits);
}

export function getDefaultCompanySettings() {
  return {
    name: 'قوائم',
    nameArabic: 'قوائم',
    activity: 'حلول تخطيط موارد المؤسسات — نظام قوائم ERP',
    services: 'ERP متكامل للشركات السعودية — مبيعات، محاسبة، مخزون، رواتب، هيئة الزكاة والضريبة والجمارك',
    address: 'طريق الملك فهد، برج الأعمال، الطابق 12',
    city: 'riyadh',
    commune: '',
    postalCode: '11564',
    country: SAUDI_COUNTRY_VALUE,
    phone: '+966 5X XXX XXXX',
    email: 'info@qawaem.ai',
    website: 'qawaemai.com',
    vatNumber: '300000000000003',
    commercialRegistration: '1010123456',
    gosiEstablishmentNumber: '1234567890',
    iban: 'SA0380000000608010167519',
    /** Legacy field aliases kept for document templates */
    nif: '300000000000003',
    nis: '',
    rc: '1010123456',
    rc2: '',
    rc3: '',
    activeRC: 'rc1',
    articleNumber: '',
    bp: '',
    bankAccount1: 'SA0380000000608010167519 — البنك الأهلي السعودي',
    bankAccount2: '',
    bankAccount3: '',
    bankAccount4: '',
    bankAccount5: '',
    bankAccount6: '',
    bankAccountsArray: [
      {
        iban: 'SA0380000000608010167519',
        bank: 'البنك الأهلي السعودي',
        branch: '',
        swift: 'NCBKSAJE',
        wpsEnabled: true,
      },
    ],
    ccpAccountsArray: [],
    logo: null,
  };
}

export function getDefaultTaxSettings() {
  const vat = getBrandVatRate();
  return {
    tva: vat,
    tap: 0,
    remise: 0,
    rabais: 0,
    escompte: 0,
    transportCost: 0,
    returnCost: 0,
    guarantee: 0,
    avance: 0,
    irg: 0,
    ibs: 0,
    defaultTaxes: ['tva'],
    taxIncluded: false,
    vatEnabled: true,
  };
}

export function getDefaultRegionalSettings() {
  return {
    locale: SAUDI_SETTINGS_DEFAULTS.locale,
    language: SAUDI_SETTINGS_DEFAULTS.language,
    currency: SAUDI_SETTINGS_DEFAULTS.currency,
    currencySymbol: SAUDI_SETTINGS_DEFAULTS.currencySymbol,
    phonePrefix: SAUDI_SETTINGS_DEFAULTS.phonePrefix,
    dateFormat: SAUDI_SETTINGS_DEFAULTS.dateFormat,
    showHijri: SAUDI_SETTINGS_DEFAULTS.showHijri,
    fiscalYear: SAUDI_SETTINGS_DEFAULTS.fiscalYear,
    weekend: [...SAUDI_SETTINGS_DEFAULTS.weekend],
    workingDays: [...SAUDI_SETTINGS_DEFAULTS.workingDays],
  };
}

/** Full default settings object (Saudi). */
export function getSaudiDefaultSettings() {
  return {
    company: getDefaultCompanySettings(),
    taxes: getDefaultTaxSettings(),
    regional: getDefaultRegionalSettings(),
    invoicing: {
      invoicePrefix: 'INV',
      quotePrefix: 'QT',
      nextInvoiceNumber: 1001,
      nextQuoteNumber: 2001,
      defaultCurrency: SAUDI_SETTINGS_DEFAULTS.currency,
      documentLanguage: SAUDI_SETTINGS_DEFAULTS.language,
      dateFormat: 'dd/MM/yyyy',
      showReferenceColumn: true,
      documentCounters: {},
    },
    notifications: {
      emailNotifications: false,
      smsNotifications: false,
      pushNotifications: false,
      invoiceReminders: true,
      paymentAlerts: true,
      lowStockAlerts: false,
    },
    design: {
      headerStyle: 'modern',
      colorScheme: 'blue',
      fontFamily: 'inter',
      showLogo: true,
      logoPosition: 'left',
      headerLayout: 'horizontal',
      documentTemplate: 'professional',
      borderStyle: 'clean',
      accentColor: '#1e88e5',
      backgroundColor: '#ffffff',
      textColor: '#000000',
      showWatermark: false,
      watermarkText: '',
      footerStyle: 'minimal',
      showPageNumbers: true,
    },
  };
}
